import React, { useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Alert, Snackbar, Stack } from '@mui/material';
import { X } from 'lucide-react';
import { selectNotifications, removeNotification } from '../../store/slices/uiSlice';
import type { Notification } from '../../store/slices/uiSlice';
import { sbg } from '../../theme/sbgTokens';

const AUTO_HIDE_DURATION = 5000; // 5 seconds
/** Toasts stack at most three deep (book §11.13). Errors are never dropped: past the cap they wait for a free slot. */
const MAX_VISIBLE = 3;

/**
 * Errors stay until dismissed (book §11.13) unless a caller explicitly opts
 * in to auto-hide; any notice sent with `autoHide: false` stays as well.
 */
const isPersistent = (notification: Notification) =>
  notification.autoHide === false || (notification.type === 'error' && notification.autoHide !== true);

const CloseGlyph: React.FC = () => <X size={16} aria-hidden="true" />;

/*
 * Brand toasts (book §11.13): raised surface, 1px default border, radius 12,
 * shadow-md and a 20px status icon — the theme styles an Alert inside a
 * Snackbar exactly that way, so this file only handles stacking and timing.
 */
const Notifications: React.FC = () => {
  const notifications = useSelector(selectNotifications);
  const dispatch = useDispatch();

  // Persistent notices take slots first. Past the cap the newest of them show
  // and older ones stay in the store, out of sight, until a dismissal frees a
  // slot: they need reading, so they're never dropped. The remaining slots go
  // to the newest auto-hide notices; older ones pushed past the cap are
  // dropped from the store so they never come back later, out of order, with
  // a fresh timer.
  const { visible, overflowIds } = useMemo(() => {
    const persistent = notifications.filter(isPersistent);
    const waiting = persistent.slice(0, Math.max(0, persistent.length - MAX_VISIBLE));
    const waitingIds = new Set(waiting.map((notification) => notification.id));
    const transient = notifications.filter((notification) => !isPersistent(notification));
    const slots = Math.max(0, MAX_VISIBLE - (persistent.length - waiting.length));
    const dropped = transient.slice(0, Math.max(0, transient.length - slots));
    const droppedIds = new Set(dropped.map((notification) => notification.id));
    return {
      visible: notifications.filter((notification) => !droppedIds.has(notification.id) && !waitingIds.has(notification.id)),
      overflowIds: dropped.map((notification) => notification.id),
    };
  }, [notifications]);

  const overflowKey = overflowIds.join(',');
  useEffect(() => {
    if (!overflowKey) return;
    overflowKey.split(',').forEach((id) => dispatch(removeNotification(id)));
  }, [overflowKey, dispatch]);

  const handleClose = (id: string) => {
    dispatch(removeNotification(id));
  };

  return (
    <Stack
      role="region"
      aria-label="Notifications"
      spacing={1}
      sx={{
        position: 'fixed',
        bottom: { xs: 16, sm: 24 },
        right: { xs: 16, sm: 24 },
        width: { xs: 'calc(100% - 32px)', sm: 360 },
        zIndex: theme => theme.zIndex.snackbar,
        pointerEvents: 'none',
      }}
    >
      {visible.map((notification) => (
        <Snackbar
          key={notification.id}
          open={true}
          // Each keyed Snackbar owns its timer; unrelated notifications cannot
          // extend its lifetime. MUI also pauses while hovered or focused.
          autoHideDuration={isPersistent(notification) ? null : AUTO_HIDE_DURATION}
          onClose={(event, reason) => {
            if (reason === 'clickaway') return;
            if (reason === 'escapeKeyDown') event?.preventDefault();
            handleClose(notification.id);
          }}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          sx={{ position: 'static', width: '100%', transform: 'none', pointerEvents: 'auto' }}
        >
          <Alert
            onClose={() => handleClose(notification.id)}
            closeText="Dismiss notification"
            severity={notification.type}
            role={notification.type === 'error' ? 'alert' : 'status'}
            slots={{ closeIcon: CloseGlyph }}
            sx={{
              width: '100%',
              // The stack sets the 360px width; let the toast fill it at phone widths too.
              '.MuiSnackbar-root &': { minWidth: 0, maxWidth: 'none' },
              '& .MuiAlert-message': { minWidth: 0, overflowWrap: 'anywhere', color: sbg.text.primary },
            }}
          >
            {notification.message}
          </Alert>
        </Snackbar>
      ))}
    </Stack>
  );
};

export default Notifications;
